import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type TransformsElementProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type FieldSortConfig,
  isBindingActive,
  type OptionPickerDefaultValue,
  OPTIONS_PICKER_TAGS_DEFAULT_VALUE_OPTIONS,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputColorPaletteModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, SCHEMA_COLUMNS_SELECT_OPTION_ARRAY } from '@blaze-shared/schema-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  TAGS_BACKGROUND_THEME_KEY,
  TAGS_FOREGROUND_THEME_KEY,
} from '../../services/element-tags.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { TagsDefinitionMapper } from './ai/tags.definition-mapper';
import { TagsElementHandler } from './ai/tags.element-handler';
import { generateTagsFormField } from './generation/form-field';
import { tagsV2Migration } from './migrations/002-tags.migration';
import { tagsV3Migration } from './migrations/003-tags.migration';
import { tagsV4PaddingToMarginMigration } from './migrations/004-tags-padding-to-margin.migration';
import { TagsComponent } from './tags.component';
import { TagsPropsBuilder } from './tags.props-builder';

export { transformToTags } from './tags.transforms';
export type TagsElement = CanvasElementFrom<'tags'>;
export type TagsDef = WithProps<TagsPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    tags: TagsDef;
  }
}

const DEFAULT_WIDTH = 356;
const DEFAULT_HEIGHT = 48;
const MIN_SIZE = 40;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['min'];

registerElementDefinition({
  type: 'tags',
  componentType: TagsComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Tags',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: TagsPropsBuilder,
  generation: {
    formField: generateTagsFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      placeholder: 'content.tags.placeholder',
    },
  },
  features: {
    ai: {
      optionalInput: true,
    },
    optionsSortSupport: true,
    searchAndFiltersFieldSupport: true,
  },
  ai: {
    name: 'tags',
    handler: () => TagsElementHandler,
    definitionMapper: () => TagsDefinitionMapper,
  },

  version: 4,
  migrations: {
    2: tagsV2Migration,
    3: tagsV3Migration,
    4: tagsV4PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'optionsList',
        isDefaultDataSourceInput: true,
        bindingDefs: [
          { property: 'value', label: 'Option value field', dataType: 'String', value: true },
          { property: 'label', label: 'Option label field', dataType: 'String', display: true },
        ],
      },
      {
        name: 'selected',
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'selected', label: 'Selected value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        // Default schema before binding consists of label, value pair. If bound, this will change.
        schema: {
          id: '',
          name: 'Tags',
          columns: SCHEMA_COLUMNS_SELECT_OPTION_ARRAY,
        },
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  TransformsElementProps &
  VisibilityElementProps;

export type TagsPropsModel = PropsModel & {
  /** @deprecated */
  version: number;
  content: {
    title: ElementLabelPropsWithPositionAndWidth;
    data: {
      defaultValue: OptionPickerDefaultValue;
    };
    sort: FieldSortConfig | FieldSortConfig[];
    tags: {
      placeholder: string;
      values: string[];
      preselect?: string[];
    };
    options: {
      canAddNew?: boolean;
      readonly?: boolean;
      singleSelect?: boolean;
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labelBlock: ElementLabelStyleProps;
    input: {
      color: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
    list: {
      value: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
    tags: {
      palette: PropertiesConfigInputColorPaletteModel;
    };
  };
};

function getDefaults(): PropsOf<'tags'> {
  return {
    version: 1,
    content: {
      title: {
        label: 'Tags',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      data: {
        defaultValue: 'none',
      },
      sort: {
        field: '',
        direction: '',
      },
      tags: {
        placeholder: '',
        values: [],
        preselect: ['Option 1'],
      },
      options: {
        canAddNew: false,
        readonly: false,
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      input: {
        color: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
      list: {
        value: { color1: '$t.value', selectorType: 'solid' },
        background: { color1: '$t.fill', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
      tags: {
        palette: {
          paletteName: 'single',
          foreground: `$t.${TAGS_FOREGROUND_THEME_KEY}`,
          background: `$t.${TAGS_BACKGROUND_THEME_KEY}`,
        },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('tags', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:optionsList.enabled',
        'Data',
        [epc.fields.listItemValues('content.tags.values', 'Values')],
        [
          // Hide data source options if the element is bound to tags column
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectDataSourceField({
                key: 'optionsList',
                label: 'Values',
                sources: 'list',
                bindings: [
                  {
                    property: 'value',
                    label: 'Option value field',
                    dataType: 'String',
                    value: true,
                  },
                  {
                    property: 'label',
                    label: 'Option label field',
                    dataType: 'String',
                    display: true,
                  },
                ],
              }),
              epc.fields.selectFieldFromSchema(
                'data.?key:optionsList.bindings.label',
                'Display',
                'data.?key:optionsList.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.fields.sortField(
                'content.sort',
                'Sort by',
                'data.?key:optionsList.dataSource.columns',
                'name',
                COLUMN_ID_OR_NAME,
              ),
            ],
            'data.?key:optionsList',
            (value) => !isBindingActive(value) || value?.dataSource?.sourceType === 'column-tags',
          ),
        ],
      ),

      epc.subsections.subsection(
        'content.tags',
        'Value',
        [
          epc.utils.conditionalField(
            epc.fields.selectFieldFromList(
              'content.data.defaultValue',
              'Default value',
              OPTIONS_PICKER_TAGS_DEFAULT_VALUE_OPTIONS,
              {
                showSelection: true,
                allowNone: false,
                optionsFilter: (model: PropsOf<'tags'>, options) =>
                  !getValueByPath(model, 'data.?key:optionsList.enabled')
                    ? options
                    : (options as SelectOption<OptionPickerDefaultValue>[])?.filter(
                        (o) => o.value !== 'fixedValue',
                      ),
              },
            ),
            'data.?key:optionsList.enabled',
            (value) => !!value, // Hide when only 'none' option is available
          ),
          epc.utils.conditionalField(
            epc.fields.textInput('content.tags.placeholder', 'Placeholder'),
            'content.data.defaultValue',
            (modelValue) => (modelValue as OptionPickerDefaultValue) !== 'placeholder',
          ),
          epc.utils.conditionalField(
            // pick from list of `values`
            epc.fields.selectFieldFromModel(
              'content.tags.preselect',
              'Value',
              'content.tags.values',
              {
                allowNone: false,
                showSelection: true,
                allowMultiSelect: true,
                optionsFilter: epc.utils.columnFormatFilter('!secureText'),
              },
            ),
            'content.data.defaultValue',
            (modelValue) => (modelValue as OptionPickerDefaultValue) !== 'fixedValue',
          ),
          epc.utils.observedField(
            epc.fields.toggleField('content.options.readonly', 'Read Only'),
            (_, readonly: boolean) => {
              return {
                ['style.input.border.color1']: readonly ? 'transparent' : '$t.outline',
                ['style.input.border.selectorType']: 'solid',
                ['style.input.fill.color1']: readonly ? 'transparent' : '$t.fillInternal',
              };
            },
          ),
        ],
        {
          dataBindings: [
            {
              key: 'selected',
              label: 'Selected Value',
              sources: 'field',
              bindings: [{ property: 'selected', label: 'Selected value', dataType: 'String' }],
            },
          ],
        },
      ),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.input', 'Input', [
          epc.fields.colorField('style.input.color', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
        ]),

        epc.subsections.subsection('style.list', 'List', [
          epc.fields.colorField('style.list.value', 'Value', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.list.background', 'Background', { allowNone: false }),
          epc.fields.colorField('style.list.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.list.radius'),
        ]),

        epc.subsections.subsection('style.tags', 'Tag Color', [
          epc.fields.tagsColorPaletteField('style.tags.palette', '', {
            bindingKey: 'optionsList',
            hideLabel: true,
          }),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}

function getInitial(): PartialPropsOf<'tags'> {
  return {
    version: 2,
    content: {
      title: {
        width: '35%',
      },
      tags: {
        values: ['Option 1', 'Option 2', 'Option 3'],
      },
    },
  };
}
